// src\themes\tolica\utils\productPriceExcerpt.ts
import { formatPrice } from "@/utils/formatUtils";
import { getSiteConfig } from "@/core/config/theme-config";

// ادمین‌های مختلف این کلمه رو با حروف بزرگ/کوچیک متفاوت می‌نویسن (TOKEN، token، Token...)
const PRICE_TOKEN_REGEX = /token/i;

// این فرمول (پارچه + مالیات روی قیمت نمایشی) فقط برای کسب‌وکار تولیکا طراحی
// شده. تم «tolica» رو چند کسب‌وکار دیگه هم به‌اشتراک می‌ذارن (مثلاً وندا، با
// NEXT_PUBLIC_THEME=tolica ولی NEXT_PUBLIC_SITE_NAME=vanda) که فرمول توکن رو
// ندارن و قیمتشون باید دقیقاً همون product.price/final_price خام بمونه؛ وگرنه
// اگه این فرمول رو بدون این چک برای همه‌ی سایت‌های تم تولیکا اجرا کنیم، قیمت
// سایت‌های دیگه هم (مثلاً با مالیاتی که برای چیز دیگه‌ای تنظیم شده) به‌هم می‌ریزه.
// توجه: مقدار واقعی NEXT_PUBLIC_SITE_NAME برای سایت تولیکا خودِ "tolica" نیست،
// "tolica_front"ـه (طبق .env) — اینو با siteName چک می‌کنیم نه فقط تم.
const TOKEN_FORMULA_SITE_NAME = "tolica_front";

function isTokenFormulaSite(): boolean {
  return getSiteConfig().siteName === TOKEN_FORMULA_SITE_NAME;
}

/**
 * فیلد excerpt محصول متن دستیِ ادمینه (مثلاً «قیمت مبل ... : با پارچه یک
 * میلیون و پانصد هزار تومان TOKEN تومان») که خودِ کلمه‌ی TOKEN توش به‌عنوان
 * جای‌گذارِ قیمت محاسبه‌شده نوشته شده. اینجا فقط همون TOKEN با عددِ محاسبه‌شده
 * جایگزین می‌شه؛ بقیه‌ی متن (از جمله نرخ پارچه به حروف) دست‌نخورده و همون
 * چیزیه که ادمین نوشته. این محاسبه فقط برای نمایشه و روی قیمت سبد
 * خرید/فاکتور اثری نداره.
 * فرمول: (قیمت محصول به تومان + متراژ پارچه × نرخ پارچه) × (۱ + درصد مالیات ÷ ۱۰۰)
 * — مالیات روی قیمتِ همه‌ی محصولات اعمال می‌شه (نه فقط پارچه‌دارها)؛ جمله‌ی
 * «متراژ پارچه × نرخ پارچه» فقط وقتی محصول پارچه داره اضافه می‌شه.
 * product.price تو دیتابیس به ریاله ولی نرخ پارچه (fabric_rate_number) و
 * متن excerpt (که خودش کلمه‌ی «تومان» رو داره) به تومانن، برای همین قیمت
 * پایه قبل از جمع با نرخ پارچه به تومان تبدیل می‌شه.
 */
const RIAL_PER_TOMAN = 10;

function hasFabricPricing(
  product: { fabric_area?: string | number | null },
  settings?: { fabric_rate_number?: string | null }
): boolean {
  const fabricArea = Number(product.fabric_area);
  const fabricRateNumber = Number(settings?.fabric_rate_number);
  return Boolean(fabricArea && fabricRateNumber);
}

/**
 * قیمت نهاییِ محصول با مالیات (و، اگه محصول متراژ پارچه داشته باشه، به‌علاوه‌ی
 * هزینه‌ی پارچه)، به تومان. همیشه عدده، چون مالیات روی قیمتِ همه‌ی محصولات
 * اعمال می‌شه؛ قیمتِ خامِ product.price جایی مستقیم نمایش داده نمی‌شه.
 */
export function getFabricInclusivePriceToman(
  product: { price?: string | number | null; fabric_area?: string | number | null },
  settings?: { fabric_rate_number?: string | null; vat_percentage?: string | null }
): number {
  const basePriceToman = (Number(product.price) || 0) / RIAL_PER_TOMAN;
  if (!isTokenFormulaSite()) return basePriceToman;

  const fabricCost = hasFabricPricing(product, settings)
    ? Number(product.fabric_area) * Number(settings?.fabric_rate_number)
    : 0;

  const priceWithFabric = basePriceToman + fabricCost;
  const vatPercentage = Number(settings?.vat_percentage) || 0;
  // گرد به نزدیک‌ترین تومان تا بعد از ضرب در درصد مالیات، عددِ اعشاریِ
  // مصنوعی (مثلاً ۱۲۴,۹۲۰,۳۳۸.۴ ریال) تو قیمتِ نمایشی ظاهر نشه.
  return Math.round(priceWithFabric * (1 + vatPercentage / 100));
}

/**
 * همون قیمت (با مالیات و، در صورت وجود، پارچه)، ولی به ریال — تا بشه جای
 * product.price/final_price (که همه‌جای سایت به ریال نشون داده می‌شن) مستقیم
 * گذاشتش، بدون به‌هم‌ریختن واحد پول تو کارت محصول.
 */
export function getFabricInclusivePriceRial(
  product: { price?: string | number | null; fabric_area?: string | number | null },
  settings?: { fabric_rate_number?: string | null; vat_percentage?: string | null }
): number {
  return getFabricInclusivePriceToman(product, settings) * RIAL_PER_TOMAN;
}

/**
 * قیمتِ نهاییِ قابل‌نمایش وقتی تخفیف هم فعاله: همون قیمتِ با‌مالیات (و پارچه)
 * بالا، منهای همون درصد تخفیفِ محصول (discount_percent) — تا نسبتِ قیمتِ
 * خط‌خورده به قیمتِ نهایی هر جای سایت دقیقاً برابرِ درصد تخفیف بمونه، حتی بعد
 * از اضافه‌شدن مالیات.
 */
export function getFabricInclusiveFinalPriceRial(
  product: {
    price?: string | number | null;
    final_price?: string | number | null;
    fabric_area?: string | number | null;
    discount_percent?: number | string | null;
  },
  settings?: { fabric_rate_number?: string | null; vat_percentage?: string | null }
): number {
  if (!isTokenFormulaSite()) return Number(product.final_price) || 0;

  const fullPrice = getFabricInclusivePriceRial(product, settings);
  const discountPercent = Number(product.discount_percent) || 0;
  return Math.round(fullPrice * (1 - discountPercent / 100));
}

// عنوان کامل محصول معمولاً مشخصات عددی (سایز/ارتفاع/عرض/طول) و برند/مدل رو هم
// داره («... سایز ۱۸۵ ارتفاع ۱۵۲ تولیکا مدل ورتا»)؛ برای برچسب کوتاه، هرچی از
// اولین عددِ مشخصات یا از «تولیکا» به بعده (هرکدوم زودتر بیاد) حذف می‌شه و فقط
// نوع محصول می‌مونه (مثلاً «تخت خواب دو نفره»). عددهای نوشته‌شده با حروف مثل
// «دو نفره» دست‌نخورده می‌مونن چون رقم نیستن.
const SHORT_TITLE_CUTOFF_REGEX = /\s*(?:(?:سایز|ارتفاع|عرض|طول)?\s*[۰-۹0-9]+[\s\S]*|تولیکا[\s\S]*)$/;

function getShortProductTitle(title?: string | null): string {
  return (title ?? '').replace(SHORT_TITLE_CUTOFF_REGEX, '').trim();
}

/**
 * برچسبِ توضیحیِ «قیمت [نوع محصول] : با پارچه [نرخ پارچه به حروف] تومان» که
 * کاملاً خودکار از عنوان محصول و نرخ پارچه‌ی تنظیمات ساخته می‌شه — دیگه نیازی
 * نیست ادمین این متن رو دستی تو excerpt بنویسه. عددی توش نیست چون خودِ عدد
 * همین الان تو قیمتِ بالای کارت (با getFabricInclusivePriceRial) نشون داده می‌شه.
 */
export function getFabricPriceLabel(
  product: { title?: string | null; price?: string | number | null; fabric_area?: string | number | null },
  settings?: { fabric_rate_number?: string | null; fabric_rate_words?: string | null; vat_percentage?: string | null }
): string | null {
  if (!isTokenFormulaSite() || !hasFabricPricing(product, settings) || !settings?.fabric_rate_words) return null;

  const shortTitle = getShortProductTitle(product.title);
  return `قیمت ${shortTitle} : با پارچه ${settings.fabric_rate_words}`;
}

export function renderProductExcerpt(
  product: { excerpt?: string | null; price?: string | number | null; fabric_area?: string | number | null },
  settings?: { fabric_rate_number?: string | null; vat_percentage?: string | null }
): string | null {
  const excerpt = product.excerpt;
  if (!excerpt) return null;
  if (!PRICE_TOKEN_REGEX.test(excerpt)) return excerpt;
  if (!isTokenFormulaSite() || !hasFabricPricing(product, settings)) return null;

  const finalPrice = getFabricInclusivePriceToman(product, settings);
  return excerpt.replace(new RegExp(PRICE_TOKEN_REGEX, "gi"), formatPrice(finalPrice));
}
